import { renderManagedEmail } from "./email-templates.server";
import type { EmailDraft } from "./email-templates";
import { requireAdmin } from "./auth-guards.server";
import { createServerFn } from "@tanstack/react-start";

const EMAIL_ENDPOINT = "https://cztnmcpjvcizqqlofakf.supabase.co/functions/v1/workspace-send-email";

const SITE_URL = "https://bizaclia.com";
const FROM = "Bizaclia <noreply@bizaclia.com>";

async function sendViaWorkspace(to: string, draft: EmailDraft) {
  const { subject, html } = await renderManagedEmail(draft);
  const apiKey = process.env.WORKSPACE_EMAIL_API_KEY;
  if (!apiKey) throw new Error("WORKSPACE_EMAIL_API_KEY is not configured");
  const res = await fetch(EMAIL_ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ from: FROM, to: [to], subject, html }),
  });
  const raw = await res.text();
  if (!res.ok) {
    console.error("[auth-email] send failed", res.status, raw);
    throw new Error(`workspace-send-email ${res.status}`);
  }
}

/**
 * Registra al usuario en Supabase (admin) sin que Supabase envíe su email
 * por defecto, genera el link de confirmación y manda nuestro email branded.
 * Devuelve { ok, needsConfirm, alreadyExists } para que el cliente reaccione.
 */
function capitalize(s: string) {
  return s
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export const signUpAndSendConfirmation = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { email: string; password: string; metadata?: Record<string, unknown> }) => {
      if (!data?.email) throw new Error("Missing email");
      if (!data?.password || data.password.length < 6) throw new Error("Password too short");
      const meta = { ...(data.metadata ?? {}) } as Record<string, unknown>;
      if (typeof meta.nombre === "string" && meta.nombre.trim()) {
        meta.nombre = capitalize(meta.nombre.trim());
      }
      if (typeof meta.apellidos === "string" && meta.apellidos.trim()) {
        meta.apellidos = capitalize(meta.apellidos.trim());
      }
      return {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        metadata: meta,
      };
    },
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // 1) Crear usuario sin confirmar (Supabase NO manda email aquí)
    const { error: createErr } = await supabaseAdmin.auth.admin.createUser({
      email: data.email,
      password: data.password,
      email_confirm: false,
      user_metadata: data.metadata,
    });

    if (createErr) {
      const msg = (createErr.message || "").toLowerCase();
      if (msg.includes("already") || msg.includes("registered") || msg.includes("exists")) {
        return { ok: false as const, alreadyExists: true as const };
      }
      console.error("[signUpAndSendConfirmation] createUser", createErr);
      throw new Error(createErr.message);
    }

    // 2) Generar link de confirmación
    const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
      type: "signup",
      email: data.email,
      password: data.password,
      options: { redirectTo: `${SITE_URL}/` },
    });

    if (linkErr || !linkData?.properties?.hashed_token) {
      console.error("[signUpAndSendConfirmation] generateLink", linkErr);
      // Usuario ya creado; devolvemos ok parcial sin email
      return { ok: true as const, emailSent: false as const };
    }

    const confirmUrl = `${SITE_URL}/confirm?token_hash=${encodeURIComponent(linkData.properties.hashed_token)}&type=signup`;

    // 3) Enviar email branded por nuestro endpoint
    try {
      await sendViaWorkspace(data.email, {
        key: "registro_confirmacion",
        variables: { confirm_url: confirmUrl },
      });
      return { ok: true as const, emailSent: true as const };
    } catch (err) {
      console.error("[signUpAndSendConfirmation] sendViaWorkspace", err);
      return { ok: true as const, emailSent: false as const };
    }
  });

export const sendPasswordResetEmail = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => {
    if (!data?.email) throw new Error("Missing email");
    return { email: data.email };
  })
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: linkData, error } = await supabaseAdmin.auth.admin.generateLink({
        type: "recovery",
        email: data.email,
        options: { redirectTo: `${SITE_URL}/reset-password` },
      });
      if (error || !linkData?.properties?.hashed_token) {
        console.error("[sendPasswordResetEmail] generateLink", error);
        return { ok: false as const };
      }
      const ctaUrl = `${SITE_URL}/reset-password?token_hash=${encodeURIComponent(linkData.properties.hashed_token)}&type=recovery`;
      await sendViaWorkspace(data.email, {
        key: "password_reset",
        variables: { reset_url: ctaUrl },
      });
      return { ok: true as const };
    } catch (err) {
      console.error("[sendPasswordResetEmail] error", err);
      return { ok: false as const };
    }
  });

/**
 * Envía email notificando acceso VIP concedido. Diseño consistente con
 * el resto de emails transaccionales de Bizaclia.
 */
export async function sendVipAccessGrantedEmail(to: string, nombre?: string | null) {
  const greeting = nombre && nombre.trim() ? `Hola ${capitalize(nombre.trim())},` : "Hola,";
  await sendViaWorkspace(to, { key: "vip_concedido", variables: { saludo: greeting } });
}

export const sendVipGrantedEmailFn = createServerFn({ method: "POST" })
  .inputValidator((data: { to: string; nombre?: string | null }) => {
    if (!data?.to) throw new Error("Missing recipient");
    return { to: data.to.trim().toLowerCase(), nombre: data.nombre ?? null };
  })
  .handler(async ({ data }) => {
    await requireAdmin();
    try {
      await sendVipAccessGrantedEmail(data.to, data.nombre);
      return { ok: true as const };
    } catch (err) {
      console.error("[sendVipGrantedEmailFn]", err);
      return { ok: false as const };
    }
  });

/**
 * Registra al usuario (sin confirmar) y envía un código OTP de 6 dígitos
 * para verificar el email sin sacar al usuario del flujo actual.
 */
export const signUpAndSendOtp = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { email: string; password: string; metadata?: Record<string, unknown> }) => {
      if (!data?.email) throw new Error("Missing email");
      if (!data?.password || data.password.length < 6) throw new Error("Password too short");
      const meta = { ...(data.metadata ?? {}) } as Record<string, unknown>;
      if (typeof meta.nombre === "string" && meta.nombre.trim()) {
        meta.nombre = capitalize(meta.nombre.trim());
      }
      if (typeof meta.apellidos === "string" && meta.apellidos.trim()) {
        meta.apellidos = capitalize(meta.apellidos.trim());
      }
      return {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        metadata: meta,
      };
    },
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error: createErr } = await supabaseAdmin.auth.admin.createUser({
      email: data.email,
      password: data.password,
      email_confirm: false,
      user_metadata: data.metadata,
    });

    if (createErr) {
      const msg = (createErr.message || "").toLowerCase();
      if (msg.includes("already") || msg.includes("registered") || msg.includes("exists")) {
        return { ok: false as const, alreadyExists: true as const };
      }
      console.error("[signUpAndSendOtp] createUser", createErr);
      throw new Error(createErr.message);
    }

    const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
      type: "signup",
      email: data.email,
      password: data.password,
      options: { redirectTo: `${SITE_URL}/` },
    });

    const code = linkData?.properties?.email_otp;
    if (linkErr || !code) {
      console.error("[signUpAndSendOtp] generateLink", linkErr);
      return { ok: true as const, emailSent: false as const };
    }

    try {
      await sendViaWorkspace(data.email, { key: "registro_otp", variables: { codigo: code } });
      return { ok: true as const, emailSent: true as const };
    } catch (err) {
      console.error("[signUpAndSendOtp] sendViaWorkspace", err);
      return { ok: true as const, emailSent: false as const };
    }
  });

/**
 * Reenvía el código OTP a un usuario ya creado pero no confirmado.
 */
export const resendSignupOtp = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => {
    if (!data?.email) throw new Error("Missing email");
    return { email: data.email.trim().toLowerCase() };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: linkData, error } = await supabaseAdmin.auth.admin.generateLink({
      type: "signup",
      email: data.email,
      password: crypto.randomUUID(),
      options: { redirectTo: `${SITE_URL}/` },
    });
    const code = linkData?.properties?.email_otp;
    if (error || !code) {
      console.error("[resendSignupOtp] generateLink", error);
      return { ok: false as const };
    }
    try {
      await sendViaWorkspace(data.email, {
        key: "registro_otp_reenvio",
        variables: { codigo: code },
      });
      return { ok: true as const };
    } catch (err) {
      console.error("[resendSignupOtp] send", err);
      return { ok: false as const };
    }
  });
